/**
 * DEMO DATABASE - 6-SYSTEM FINANCIAL ARCHITECTURE
 * 
 * This module provides a comprehensive in-memory database with:
 * - 6 interconnected financial systems with referential integrity
 * - 2 years of historical data + 90 days forecast + 3 years budget
 * - 150 employees across 8 entities
 * - Full drill-down support (every number traces to source)
 * - Entity filtering (Birmingham HQ + 7 regional offices)
 * 
 * Date Baseline: December 5, 2025
 * Company: BuildRight Construction Ltd
 */

import {
  BankAccount,
  Invoice,
  Bill,
  Transaction,
  TransactionCategory,
  ConfidenceLevel,
  InvoiceStatus,
  BillStatus,
} from '../types';

import { generateFinancialData, type FinancialSystemsData } from './financial-systems';

// ============================================================================
// GENERATE ALL FINANCIAL DATA FROM 6-SYSTEM ARCHITECTURE
// ============================================================================

console.log('🚀 Generating financial data from 6-system architecture...');
const FINANCIAL_DATA: FinancialSystemsData = generateFinancialData();
console.log('✅ Financial data generation complete!');

// ============================================================================
// EXTRACT DATA FOR BACKWARD COMPATIBILITY
// ============================================================================

// Master Data
export const ENTITIES = FINANCIAL_DATA.entities;
export const CUSTOMERS = FINANCIAL_DATA.customers;
export const SUPPLIERS = FINANCIAL_DATA.suppliers;
export const EMPLOYEES = FINANCIAL_DATA.employees;
export const PROJECTS = FINANCIAL_DATA.projects;
export const BANK_ACCOUNTS = FINANCIAL_DATA.bankAccounts;

// System 1: Accounting
// Map invoices to add customer name and calculate daysOverdue
export const INVOICES: Invoice[] = FINANCIAL_DATA.invoices.map(inv => {
  const customer = FINANCIAL_DATA.customers.find(c => c.id === inv.customerId);
  const today = new Date();
  const daysOverdue = inv.status !== 'paid' && inv.dueDate < today
    ? Math.floor((today.getTime() - inv.dueDate.getTime()) / (1000 * 60 * 60 * 24))
    : undefined;
  
  return {
    ...inv,
    customer: customer?.name || 'Unknown Customer',
    daysOverdue,
  } as Invoice;
});

// Map bills to add supplier name and payment terms
export const BILLS: Bill[] = FINANCIAL_DATA.bills.map(bill => {
  const supplier = FINANCIAL_DATA.suppliers.find(s => s.id === bill.supplierId);
  
  return {
    ...bill,
    supplier: supplier?.name || 'Unknown Supplier',
    paymentTerms: supplier?.paymentTerms || 30,
  } as Bill;
});

// System 2: Payroll
export const PAYROLL_RUNS = FINANCIAL_DATA.payrollRuns;
export const TAX_PAYMENTS = FINANCIAL_DATA.taxPayments;

// System 3: Time Recording
export const TIME_SHEETS = FINANCIAL_DATA.timeSheets;
export const UNBILLED_TIME = FINANCIAL_DATA.unbilledTime;

// System 4: Purchase & Sales Orders
export const PURCHASE_ORDERS = FINANCIAL_DATA.purchaseOrders;
export const SALES_ORDERS = FINANCIAL_DATA.salesOrders;

// System 5: CRM Pipeline
export const OPPORTUNITIES = FINANCIAL_DATA.opportunities;

// System 6: Budget
export const MONTHLY_BUDGETS = FINANCIAL_DATA.monthlyBudgets;

// ============================================================================
// SOURCE PROVENANCE MAPPING
// Maps internal source system keys → human-readable data source names
// for audit trail display in drill-down screens.
//
// In live mode: the data source adapter pipeline stamps its own name on
// every transaction record — no mapping needed.
// In demo mode: we map the machine key to the product name configured in
// Admin → Data Sources for BuildRight's demo setup.
// ============================================================================

export const SOURCE_SYSTEM_NAMES: Record<string, string> = {
  'accounting':     'Access Financials',
  'payroll':        'Paycircle',
  'time-recording': 'Access Timesheets',
  'purchase-order': 'Access Financials',   // POs live in the same accounting system
  'sales-order':    'Access Financials',   // SOs live in the same accounting system
  'crm':            'Access CRM',
  'budget':         'Access Financials',   // Budget data comes from the finance system
  'banking':        'HSBC Business Banking',
};

/**
 * Resolve a machine source system key to a human-readable data source name.
 * Falls back gracefully if the key isn't in the map.
 */
export function resolveDataSourceName(sourceSystem?: string): string | undefined {
  if (!sourceSystem) return undefined;
  return SOURCE_SYSTEM_NAMES[sourceSystem] ?? sourceSystem;
}

// ============================================================================
// CONVERT BANK TRANSACTIONS TO LEGACY TRANSACTION FORMAT
// ============================================================================

/**
 * Derive a live-compatible status from bank transaction data.
 * Mirrors the SQL view status derivation logic.
 */
function deriveTransactionStatus(bt: { status: string; date: Date; amount: number }): string {
  // If already a live-derived status, pass through
  if (['Settled', 'Outstanding', 'Overdue', 'Unknown'].includes(bt.status)) return bt.status;
  // Legacy mapping for any remaining old-style statuses
  if (bt.status === 'completed') return 'Settled';
  if (bt.status === 'pending' || bt.status === 'scheduled') return 'Outstanding';
  return bt.status;
}

/**
 * Map source system keys to the unified Source column values.
 */
function resolveSourceLabel(sourceSystem: string): string {
  switch (sourceSystem) {
    case 'accounting': return 'Sales Ledger';
    case 'payroll': return 'Purchase Ledger';
    case 'purchase-order': return 'Purchase Order';
    case 'sales-order': return 'Sales Order';
    case 'crm': return 'Sales Ledger';
    case 'budget': return 'Bank';
    case 'time-recording': return 'Sales Ledger';
    default: return 'Bank';
  }
}

/**
 * Convert BankTransaction to Transaction format for backward compatibility
 * This ensures existing UI components continue to work.
 * dataSourceName is resolved from BankTransaction.sourceSystem for audit trail.
 * Status is derived to match the live SQL view logic.
 */
export const TRANSACTIONS: Transaction[] = FINANCIAL_DATA.bankTransactions.map(bt => ({
  id: bt.id,
  date: bt.date,
  description: bt.description,
  amount: bt.amount,
  type: bt.type,
  category: bt.category,
  accountId: bt.accountId || 'acc-main', // Default to main account
  status: deriveTransactionStatus(bt),
  confidence: bt.confidence,
  entity: bt.entity,
  source: resolveSourceLabel(bt.sourceSystem),
  reference: bt.sourceRecordId,
  accountName: bt.type === 'income' ? 'Customer Account' : 'Supplier Account',
  transactionType: bt.type === 'income' ? 'Receipt' : 'Payment',
  
  // Link to source records for drill-down
  invoiceId: bt.invoiceId,
  billId: bt.billId,

  // SOURCE PROVENANCE — human-readable name for audit trail display
  dataSourceName: resolveDataSourceName(bt.sourceSystem),
  
  // Add source system metadata for traceability
  metadata: {
    sourceSystem: bt.sourceSystem,
    sourceRecordId: bt.sourceRecordId,
    payrollRunId: bt.payrollRunId,
  },
}));

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

export const BASE_DATE = FINANCIAL_DATA.metadata.baselineDate;
export const DAYS_IN_FORECAST = 90;

// ============================================================================
// QUERY FUNCTIONS (Backward Compatible)
// ============================================================================

/**
 * Get transactions within a date range
 */
export const getTransactionsByDateRange = (
  startDate: Date,
  endDate: Date
): Transaction[] => {
  return TRANSACTIONS.filter(
    (t) => t.date >= startDate && t.date <= endDate
  );
};

/**
 * Get transactions for a specific date
 */
export const getTransactionsByDate = (date: Date): Transaction[] => {
  const dateStr = date.toISOString().split('T')[0];
  return TRANSACTIONS.filter(
    (t) => t.date.toISOString().split('T')[0] === dateStr
  );
};

/**
 * Get transactions by category
 */
export const getTransactionsByCategory = (category: TransactionCategory): Transaction[] => {
  return TRANSACTIONS.filter((t) => t.category === category);
};

/**
 * Get transactions by type (income/expense)
 */
export const getTransactionsByType = (type: 'income' | 'expense'): Transaction[] => {
  return TRANSACTIONS.filter((t) => t.type === type);
};

/**
 * Get transactions by bank account
 */
export const getTransactionsByAccount = (accountId: string): Transaction[] => {
  return TRANSACTIONS.filter((t) => t.accountId === accountId);
};

/**
 * Get transactions by entity (for entity filtering)
 */
export const getTransactionsByEntity = (entity: string): Transaction[] => {
  return TRANSACTIONS.filter((t) => t.entity === entity);
};

/**
 * Get transactions linked to an invoice
 */
export const getTransactionsByInvoice = (invoiceId: string): Transaction[] => {
  return TRANSACTIONS.filter((t) => t.invoiceId === invoiceId);
};

/**
 * Get transactions linked to a bill
 */
export const getTransactionsByBill = (billId: string): Transaction[] => {
  return TRANSACTIONS.filter((t) => t.billId === billId);
};

/**
 * Get transactions by source system
 */
export const getTransactionsBySourceSystem = (sourceSystem: string): Transaction[] => {
  return TRANSACTIONS.filter((t) => t.metadata?.sourceSystem === sourceSystem);
};

/**
 * Calculate cash flow for a date range
 */
export const calculateCashFlow = (startDate: Date, endDate: Date) => {
  const transactions = getTransactionsByDateRange(startDate, endDate);
  
  const income = transactions
    .filter((t) => t.type === 'income' && t.status === 'completed')
    .reduce((sum, t) => sum + t.amount, 0);
  
  const expenses = transactions
    .filter((t) => t.type === 'expense' && t.status === 'completed')
    .reduce((sum, t) => sum + t.amount, 0);
  
  return {
    income,
    expenses,
    netCashFlow: income - expenses,
  };
};

/**
 * Calculate forecast balance from a start date
 */
export const calculateForecastBalance = (
  startDate: Date,
  startingBalance: number
): { date: Date; balance: number; confidence: ConfidenceLevel }[] => {
  const futureTransactions = TRANSACTIONS.filter((t) => t.date >= startDate)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
  
  let balance = startingBalance;
  const forecast: { date: Date; balance: number; confidence: ConfidenceLevel }[] = [];
  
  futureTransactions.forEach((t) => {
    if (t.type === 'income') {
      balance += t.amount;
    } else {
      balance -= t.amount;
    }
    
    forecast.push({
      date: t.date,
      balance,
      confidence: t.confidence || 'certain',
    });
  });
  
  return forecast;
};

/**
 * Get overdue invoices
 */
export const getOverdueInvoices = (): Invoice[] => {
  const today = new Date();
  return INVOICES.filter(
    (inv) => inv.status !== 'paid' && inv.dueDate < today
  );
};

/**
 * Get overdue bills
 */
export const getOverdueBills = (): Bill[] => {
  const today = new Date();
  return BILLS.filter(
    (bill) => bill.status !== 'paid' && bill.dueDate < today
  );
};

/**
 * Get total cash across all bank accounts
 */
export const getTotalCash = (): number => {
  return BANK_ACCOUNTS.reduce((sum, acc) => sum + acc.balance, 0);
};

/**
 * Get summary statistics
 */
export const getSummaryStats = () => {
  const totalOutstandingInvoices = INVOICES
    .filter((inv) => inv.status !== 'paid')
    .reduce((sum, inv) => sum + inv.amount, 0);
  
  const totalOutstandingBills = BILLS
    .filter((bill) => bill.status !== 'paid')
    .reduce((sum, bill) => sum + bill.amount, 0);
  
  const overdueInvoices = getOverdueInvoices();
  const overdueBills = getOverdueBills();
  
  return {
    totalCash: getTotalCash(),
    totalOutstandingInvoices,
    totalOutstandingBills,
    overdueInvoicesCount: overdueInvoices.length,
    overdueInvoicesValue: overdueInvoices.reduce((sum, inv) => sum + inv.amount, 0),
    overdueBillsCount: overdueBills.length,
    overdueBillsValue: overdueBills.reduce((sum, bill) => sum + bill.amount, 0),
    netOutstanding: totalOutstandingInvoices - totalOutstandingBills,
  };
};

// ============================================================================
// NEW QUERY FUNCTIONS FOR 6-SYSTEM ARCHITECTURE
// ============================================================================

/**
 * Get invoices by source system
 */
export const getInvoicesBySourceSystem = (sourceSystem: string): Invoice[] => {
  // Filter from original financial data which has sourceSystem field
  const filteredIds = FINANCIAL_DATA.invoices
    .filter((inv) => inv.sourceSystem === sourceSystem)
    .map((inv) => inv.id);
  return INVOICES.filter((inv) => filteredIds.includes(inv.id));
};

/**
 * Get bills by source system
 */
export const getBillsBySourceSystem = (sourceSystem: string): Bill[] => {
  // Filter from original financial data which has sourceSystem field
  const filteredIds = FINANCIAL_DATA.bills
    .filter((bill) => bill.sourceSystem === sourceSystem)
    .map((bill) => bill.id);
  return BILLS.filter((bill) => filteredIds.includes(bill.id));
};

/**
 * Get unbilled time for a project
 */
export const getUnbilledTimeByProject = (projectId: string) => {
  return UNBILLED_TIME.find((ut) => ut.projectId === projectId);
};

/**
 * Get opportunities by stage
 */
export const getOpportunitiesByStage = (stage: string) => {
  return OPPORTUNITIES.filter((opp) => opp.stage === stage);
};

/**
 * Get weighted pipeline value
 */
export const getWeightedPipelineValue = (): number => {
  return OPPORTUNITIES.reduce((sum, opp) => sum + (opp.value * opp.probability), 0);
};

/**
 * Get budget for a specific month and entity
 */
export const getBudgetByMonthAndEntity = (month: string, entity: string) => {
  return MONTHLY_BUDGETS.filter((b) => b.month === month && b.entity === entity);
};

/**
 * Get system breakdown (how much each system contributes to forecast)
 */
export const getSystemBreakdown = () => {
  const accountingInvoices = getInvoicesBySourceSystem('accounting');
  const timeRecordingInvoices = getInvoicesBySourceSystem('time-recording');
  const salesOrderInvoices = getInvoicesBySourceSystem('sales-order');
  const crmInvoices = getInvoicesBySourceSystem('crm');
  
  const accountingValue = accountingInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const timeRecordingValue = timeRecordingInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const salesOrderValue = salesOrderInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const crmValue = crmInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const payrollValue = PAYROLL_RUNS.reduce((sum, pr) => sum + pr.totalGross, 0);
  
  const total = accountingValue + timeRecordingValue + salesOrderValue + crmValue + payrollValue;
  
  return {
    accounting: { value: accountingValue, percentage: (accountingValue / total) * 100 },
    timeRecording: { value: timeRecordingValue, percentage: (timeRecordingValue / total) * 100 },
    salesOrders: { value: salesOrderValue, percentage: (salesOrderValue / total) * 100 },
    crm: { value: crmValue, percentage: (crmValue / total) * 100 },
    payroll: { value: payrollValue, percentage: (payrollValue / total) * 100 },
    total,
  };
};

// ============================================================================
// EXPORT ALL DATA
// ============================================================================

export const DemoDatabase = {
  // Master Data
  entities: ENTITIES,
  customers: CUSTOMERS,
  suppliers: SUPPLIERS,
  employees: EMPLOYEES,
  projects: PROJECTS,
  bankAccounts: BANK_ACCOUNTS,
  
  // System 1: Accounting
  invoices: INVOICES,
  bills: BILLS,
  transactions: TRANSACTIONS,
  
  // System 2: Payroll
  payrollRuns: PAYROLL_RUNS,
  taxPayments: TAX_PAYMENTS,
  
  // System 3: Time Recording
  timeSheets: TIME_SHEETS,
  unbilledTime: UNBILLED_TIME,
  
  // System 4: Purchase & Sales Orders
  purchaseOrders: PURCHASE_ORDERS,
  salesOrders: SALES_ORDERS,
  
  // System 5: CRM Pipeline
  opportunities: OPPORTUNITIES,
  
  // System 6: Budget
  monthlyBudgets: MONTHLY_BUDGETS,
  
  // Legacy query functions (backward compatible)
  getTransactionsByDateRange,
  getTransactionsByDate,
  getTransactionsByCategory,
  getTransactionsByType,
  getTransactionsByAccount,
  getTransactionsByEntity,
  getTransactionsByInvoice,
  getTransactionsByBill,
  calculateCashFlow,
  calculateForecastBalance,
  getOverdueInvoices,
  getOverdueBills,
  getTotalCash,
  getSummaryStats,
  
  // New query functions for 6-system architecture
  getTransactionsBySourceSystem,
  getInvoicesBySourceSystem,
  getBillsBySourceSystem,
  getUnbilledTimeByProject,
  getOpportunitiesByStage,
  getWeightedPipelineValue,
  getBudgetByMonthAndEntity,
  getSystemBreakdown,
  
  // Constants
  BASE_DATE,
  DAYS_IN_FORECAST,
  
  // Metadata
  metadata: FINANCIAL_DATA.metadata,
};

export default DemoDatabase;
